import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CommonModal from 'src/components/CommonModal';
import InputText from 'src/components/InputText/InputText';
import SelectBox from 'src/components/SelectBox/SelectBox';
import { KGS_MAX_VALUES, LBS_MAX_VALUES, MEASUREMENT, STATUS } from 'src/constants/common';
import { addressDetailsData, isViewQuoteData } from 'src/redux/Quoting.slice';
import {
    getCalculatedFreightClass,
    // getCalculatedFreightClass,
    getFormattedNumber,
    getLabelByValue,
} from 'src/utils/CommonFunctions';
import { createDimensionMaxValidator } from 'src/utils/dimensionValidation';
import * as yup from 'yup';

import ThreeJsScene from '../ThreeJsScene';

const VisualizeUnit = ({
    weightMeasurement,
    lengthMeasurement,
    parentFreightClass,
    handleClose,
    currentData,
    unitArr,
    handleVisualizeUnitSubmit,
    isEnable = false,
    primaryEquipmentType = '',
    primaryEquipmentTypeFullName = '',
}: any) => {
    const { t } = useTranslation();
    const isViewQuote = useSelector(isViewQuoteData);
    const isEnableField = useMemo(() => (isEnable ? true : !isViewQuote), [isEnable, isViewQuote]);
    const addressDetails = useSelector(addressDetailsData);
    const isStatusWonOrLost = useMemo(() => addressDetails?.status === STATUS.WON || addressDetails?.status === STATUS.LOST, [addressDetails?.status]);
    const [isInitialRender, setIsInitialRender] = useState(true);

    const initialValues = useMemo(
        () => ({
            handlingUnit: currentData.data.handlingUnit || '',
            totalWeight: Number(currentData.data.totalWeight) || 0,
            itemLength: Number(currentData.data.itemLength || currentData.data.length) || 0,
            height: Number(currentData.data.height) || 0,
            width: Number(currentData.data.width) || 0,
        }),
        []
    );

    const [totalObj, setTotalObj] = useState({
        cubicFeet: 0,
        density: 0,
        freightClass: 0,
    });

    const createMaxValueValidator = (maxValueKey: keyof typeof KGS_MAX_VALUES & keyof typeof LBS_MAX_VALUES, translationKey: string) =>
        function (this: yup.TestContext, value: any) {
            const maxValue = weightMeasurement === MEASUREMENT.WEIGHT2.value ? KGS_MAX_VALUES[maxValueKey] : LBS_MAX_VALUES[maxValueKey];

            if (value === null || value === undefined) return true;

            if (value > maxValue) {
                return this.createError({
                    path: `${this.path}`,
                    message: t(translationKey, { max: maxValue } as any),
                });
            }

            return true;
        };

    const dimMaxValidator = (field: 'HEIGHT' | 'WIDTH') =>
        createDimensionMaxValidator(
            field,
            t,
            () => ({
                equipmentType: primaryEquipmentType,
                equipmentTypeFullName: primaryEquipmentTypeFullName,
                weightMeasurement,
            }),
            'VisualizeUnit'
        );

    useEffect(() => {
        console.log('primaryEquipmentType', primaryEquipmentType);
    }, [primaryEquipmentType]);

    const validationSchema = yup.object().shape({
        handlingUnit: yup.string().required('Unit is required.'),
        totalWeight: yup.number().required('Total_weight is required.').test('max', '', createMaxValueValidator('MAX_WEIGHT', 'ValidationMessages.Total_weight_must_not_exceed')),
        itemLength: yup
            .number()
            .nullable()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .required(t('ValidationMessages.Length_is_required'))
            .max(9999),
        height: yup
            .number()
            .nullable()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .required(t('ValidationMessages.Height_is_required'))
            .test('max', '', dimMaxValidator('HEIGHT')),
        width: yup
            .number()
            .nullable()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .required(t('ValidationMessages.Width_is_required'))
            .test('max', '', dimMaxValidator('WIDTH')),
    });

    const { handleSubmit, control, getValues, watch } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            handlingUnit: currentData.data.handlingUnit || '',
            totalWeight: Number(currentData.data.totalWeight) || 0,
            itemLength: Number(currentData.data.itemLength || currentData.data.length) || 0,
            height: Number(currentData.data.height) || 0,
            width: Number(currentData.data.width) || 0,
        },
    });

    const watchedValues = watch();
    const isActuallyDirty = useMemo(
        () =>
            String(watchedValues.handlingUnit) !== String(initialValues.handlingUnit) ||
            Number(watchedValues.totalWeight) !== Number(initialValues.totalWeight) ||
            Number(watchedValues.itemLength) !== Number(initialValues.itemLength) ||
            Number(watchedValues.height) !== Number(initialValues.height) ||
            Number(watchedValues.width) !== Number(initialValues.width),
        [watchedValues, initialValues]
    );

    useEffect(() => {
        const weight = Number(currentData.data.weight);
        const length = Number(getValues('itemLength'));
        const width = Number(getValues('width'));
        const height = Number(getValues('height'));

        let freightClass = 0;

        if (weight && length && width && height) {
            const calculatedFreightClass = getCalculatedFreightClass({
                itemLength: length,
                width: width,
                height: height,
                weight: weight,
                lengthUnit: lengthMeasurement,
                weightUnit: weightMeasurement,
            });

            freightClass = calculatedFreightClass?.freightClass || 0;
        }

        const cubicFeet = (Number(getValues('itemLength')) * Number(getValues('width')) * Number(getValues('height'))) / 1728;
        const density = cubicFeet > 0 ? Number(currentData.data.weight) / cubicFeet : 0;

        setTotalObj((old: any) => ({
            ...old,
            cubicFeet: cubicFeet,
            density: density,
            freightClass: isInitialRender ? (parentFreightClass ? parentFreightClass : freightClass) : freightClass,
        }));

        if (isInitialRender) {
            setIsInitialRender(false);
        }
    }, [watch('itemLength'), watch('width'), watch('height')]);

    const handleOption = (onChange: any) => (e: any) => {
        onChange(e?.value);
    };

    const onSubmit = (data: any) => {
        const index = currentData.index;
        handleVisualizeUnitSubmit(index, data);
    };

    return (
        <CommonModal
            size={'max-w-[954px]'}
            handleClose={handleClose}
            title={t('QuotingHub.QuotingDashboard.Visualize_Unit')}
            primaryBtnText={isStatusWonOrLost ? '' : t('Common.Save_Changes')}
            modalPrimaryBtnClass={!isEnableField ? 'btn_primary_disabled' : ''}
            secondaryBtnText={t('Common.Cancel')}
            secondaryBtnOnClick={handleClose}
            primaryBtnOnClick={handleSubmit(onSubmit)}
            primaryBtnDisabled={!isActuallyDirty}
        >
            <div className="p-4">
                <form onSubmit={handleSubmit(onSubmit)} className="flex md:flex-row flex-col items-center w-full">
                    <div className="flex flex-col gap-5 md:flex-1 md:mr-5 w-full md:mb-0 mb-5">
                        <Controller
                            name="handlingUnit"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <SelectBox
                                    parentClassName="transparent-disabled-select"
                                    name="handlingUnit"
                                    label={t('QuotingHub.QuotingDashboard.Select_Unit')}
                                    className={`form_control`}
                                    labelClassName="form_label block mb-1.5"
                                    id="handlingUnit"
                                    options={unitArr}
                                    onChangeFunc={handleOption(onChange)}
                                    isSearchable={false}
                                    isDisabled={!isEnableField}
                                    value={unitArr.find((handlingUnitOption: any) => handlingUnitOption.value === parseInt(value) || '')}
                                    errorText={error?.message ? error.message : null}
                                    required={true}
                                />
                            )}
                        />

                        <Controller
                            name="totalWeight"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <InputText
                                    label={`${t('Common.Total_Weight')} (${getLabelByValue(weightMeasurement)?.toLowerCase()})`}
                                    inputName="totalWeight"
                                    placeholder={t('QuotingHub.Common.Enter_total_weight')}
                                    parentClassName="flex-1"
                                    className={`flex-1 ${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                    inputWrapClass={`transparent-disabled-input`}
                                    isShowDisableClass={false}
                                    disabled={true || !isEnableField}
                                    value={value ?? 0}
                                    readOnly
                                    inputType="number"
                                    isAllowDecimal={true}
                                    maxLength={7}
                                    onChangeFunc={onChange}
                                    shouldFormatNumber={true}
                                    inputValueUnitType={getLabelByValue(weightMeasurement)?.toLowerCase()}
                                    errorText={error?.type == 'max' ? error.message : ''}
                                    isIconError={!!error?.message}
                                    labelClassName="form_label block mb-1.5"
                                />
                            )}
                        />

                        <Controller
                            name="itemLength"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <InputText
                                    label={`${t('QuotingHub.QuotingDashboard.Length')} (${getLabelByValue(lengthMeasurement)?.toLowerCase()})`}
                                    inputName="itemLength"
                                    inputType="number"
                                    placeholder={t('QuotingHub.QuotingDashboard.Enter_length')}
                                    parentClassName="flex-1"
                                    className={`flex-1 ${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                    value={value}
                                    inputWrapClass={`${!isEnableField ? 'transparent-disabled-input' : ''}`}
                                    isShowDisableClass={false}
                                    disabled={!isEnableField}
                                    maxLength={4}
                                    isAllowDecimal={true}
                                    onChangeFunc={onChange}
                                    shouldFormatNumber={true}
                                    inputValueUnitType={getLabelByValue(lengthMeasurement)?.toLowerCase()}
                                    isIconError={!!error?.message}
                                    labelClassName="form_label block mb-1.5"
                                    errorText={error?.type == 'max' ? error.message : ''}
                                    required={true}
                                />
                            )}
                        />

                        <Controller
                            name="width"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <InputText
                                    label={`${t('QuotingHub.QuotingDashboard.Width')} (${getLabelByValue(lengthMeasurement)?.toLowerCase()})`}
                                    inputName="width"
                                    inputType="number"
                                    placeholder={t('QuotingHub.QuotingDashboard.Enter_width')}
                                    parentClassName="flex-1"
                                    className={`flex-1 ${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                    value={value}
                                    shouldFormatNumber={true}
                                    inputWrapClass={`${!isEnableField ? 'transparent-disabled-input' : ''}`}
                                    isAllowDecimal={true}
                                    maxLength={4}
                                    isShowDisableClass={false}
                                    disabled={!isEnableField}
                                    inputValueUnitType={getLabelByValue(lengthMeasurement)?.toLowerCase()}
                                    onChangeFunc={onChange}
                                    isIconError={!!error?.message}
                                    labelClassName="form_label block mb-1.5"
                                    errorText={error?.type == 'max' ? error.message : ''}
                                    required={true}
                                />
                            )}
                        />

                        <Controller
                            name="height"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <InputText
                                    label={`${t('QuotingHub.QuotingDashboard.Height')} (${getLabelByValue(lengthMeasurement)?.toLowerCase()})`}
                                    inputName="height"
                                    inputType="number"
                                    placeholder={t('QuotingHub.QuotingDashboard.Enter_height')}
                                    parentClassName="flex-1"
                                    className={`flex-1 ${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                    value={value}
                                    isAllowDecimal={true}
                                    maxLength={4}
                                    onChangeFunc={onChange}
                                    shouldFormatNumber={true}
                                    inputWrapClass={`${!isEnableField ? 'transparent-disabled-input' : ''}`}
                                    isShowDisableClass={false}
                                    disabled={!isEnableField}
                                    inputValueUnitType={getLabelByValue(lengthMeasurement)?.toLowerCase()}
                                    isIconError={!!error?.message}
                                    labelClassName="form_label block mb-1.5"
                                    errorText={error?.type == 'max' ? error.message : ''}
                                    required={true}
                                />
                            )}
                        />
                    </div>
                    <div className="md:w-[50%] w-full h-auto px-5">
                        <div className="md:h-full w-full">
                            <Controller
                                name="height"
                                control={control}
                                render={() => (
                                    <ThreeJsScene height={Number(getValues('height') ?? 0) / 10} width={Number(getValues('width') ?? 0) / 10} depth={Number(getValues('itemLength') ?? 0) / 10} />
                                )}
                            />
                        </div>
                    </div>
                </form>
                <div className="w-full flex sm:flex-row flex-col -mx-2.5 sm:my-4">
                    <div className={`sm:w-1/3 rounded-lg border border-borderPrimary shadow-xs px-6 py-2 text-center mx-2.5 sm:my-0 my-2 w-full`}>
                        <h6 className="text-textSecondary text-xs font-normal mb-1">{t('QuotingHub.QuotingDashboard.Total_Volume_of_Shipment')}</h6>
                        <p className="text-primary text-xs font-semibold">
                            {getFormattedNumber(totalObj.cubicFeet)} {t('QuotingHub.QuotingDashboard.cubic_ft')}
                        </p>
                    </div>
                    <div className={`sm:w-1/3 rounded-lg border border-borderPrimary shadow-xs px-6 py-2 text-center mx-2.5 sm:my-0 my-2 w-full`}>
                        <h6 className="text-textSecondary text-xs font-normal mb-1">{t('QuotingHub.QuotingDashboard.Total_Density_of_Shipment')}</h6>
                        <p className="text-primary text-xs font-semibold">
                            {getFormattedNumber(totalObj.density)} {getLabelByValue(weightMeasurement)?.toLowerCase()}/{t('QuotingHub.QuotingDashboard.cubic_ft')}
                        </p>
                    </div>
                    <div className={`sm:w-1/3 rounded-lg border border-borderPrimary shadow-xs px-6 py-2 text-center mx-2.5 sm:my-0 my-2 w-full`}>
                        <h6 className="text-textSecondary text-xs font-normal mb-1">{t('QuotingHub.QuotingDashboard.Freight_Density_Class')}</h6>
                        <p className="text-primary text-xs font-semibold">{getFormattedNumber(totalObj.freightClass)}</p>
                    </div>
                </div>
            </div>
        </CommonModal>
    );
};

export default VisualizeUnit;
